import { useEffect, useState } from 'react';
import { ArrowLeft, Ruler, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card } from '@/Components/ui/Card';
import { Checkbox } from '@/Components/ui/Checkbox';
import { Input } from '@/Components/ui/Input';
import { PageHeader } from '@/Components/data/PageHeader';
import { Field } from '@/Components/forms/Field';
import FormStickyFooter from '@/Components/forms/FormStickyFooter';
import unidadeMedidaService, { type UnidadeMedida, type UnidadeMedidaInput } from '@/services/unidadeMedida';

const FORM_VAZIO: UnidadeMedidaInput = {
  codigo: '',
  descricao: '',
  ativo: true,
};

export default function UnidadeMedidaForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editando = Boolean(id);
  const [form, setForm] = useState<UnidadeMedidaInput>(FORM_VAZIO);
  const [loading, setLoading] = useState(editando);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    unidadeMedidaService.buscarPorId(id)
      .then((data: UnidadeMedida) => setForm({ codigo: data.codigo, descricao: data.descricao, ativo: data.ativo }))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const salvar = async () => {
    if (!form.codigo || !form.descricao) return;
    setSalvando(true);
    try {
      if (id) {
        await unidadeMedidaService.atualizar(id, form);
      } else {
        await unidadeMedidaService.criar(form);
      }
      navigate('/producao/unidades-medida');
    } catch (error) {
      console.error('Erro ao salvar unidade', error);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <form className="nx-page nx-sngpc-form-page" onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
      <PageHeader
        icon={<Ruler size={21} strokeWidth={2} />}
        title={editando ? 'Editar Unidade de Medida' : 'Nova Unidade de Medida'}
        description={editando ? 'Atualize os dados da unidade de medida.' : 'Preencha os dados para incluir uma nova unidade de medida.'}
        actions={
          <Button type="button" variant="ghost" onClick={() => navigate('/producao/unidades-medida')}>
            <ArrowLeft size={16} /> Voltar
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-form-card">
        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header">
            <h2>Identificação</h2>
          </div>
          <div className="nx-sngpc-page-form-grid">
            <Field label="Código *" htmlFor="unidade-codigo">
              <Input
                id="unidade-codigo"
                value={form.codigo || ''}
                onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                disabled={editando || loading}
                placeholder="KG"
                required
              />
            </Field>
            <Field label="Descrição *" htmlFor="unidade-descricao">
              <Input
                id="unidade-descricao"
                value={form.descricao || ''}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                disabled={loading}
                placeholder="Quilograma"
                required
              />
            </Field>
            <label className="nx-sngpc-checkbox-field">
              <Checkbox
                checked={form.ativo ?? true}
                onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                disabled={loading}
              />
              <span>Ativo</span>
            </label>
          </div>
        </section>
      </Card>

      <FormStickyFooter
        cancelLabel="Cancelar"
        onCancel={() => navigate('/producao/unidades-medida')}
        saveLabel="Salvar"
        loading={salvando}
        saveIcon={<Save size={16} />}
      />
    </form>
  );
}
